/* gara-recs.js — fitment-aware "You may also like" (Benjamin 2026-08-23).
 *
 * With a bike selected (GaraYMM), the PDP strip fills with parts that FIT it,
 * walked until FULL (always up to the section count):
 *   P1  the product's own SUBCATEGORY collection pool
 *   P2  its TOP category collection pool
 *   P3  the Top Picks margin pool (/collections/recs-top-picks, margin order)
 *   P4  INDEX-RESOLVE: every remaining fitting SKU straight from the product
 *       index (closest-category first), handles via the lazy-loaded
 *       gara-sku-handles.js, each card server-rendered by its own product page
 *       (?sections=gara-recs-card) — reaches items buried mid-collection that
 *       no pool page can sample
 *   P5  maintenance that fits, then tires matching the bike's stock sizes
 *   P6  universal-fit top-up from pools already in hand (honest "fits")
 * No bike selected -> the native Shopify strip is untouched. Zero results
 * anywhere -> native strip stands (never an empty section).
 * Pools + cards come from the Section Rendering API; card markup is the same
 * product-grid-item used site-wide.
 */
(function () {
  'use strict';
  var host = document.querySelector('[data-gara-recs]');
  if (!host) return;

  var LIMIT = parseInt(host.getAttribute('data-limit') || '6', 10) || 6;
  var SELF_HANDLE = host.getAttribute('data-product-handle') || '';
  var SUBCAT = host.getAttribute('data-subcat-handle') || '';
  var TOPCAT = host.getAttribute('data-topcat-handle') || '';
  var HEADING = host.getAttribute('data-heading') || 'You may also like';
  var SUB_TAG = SUBCAT.replace(/^moto-/, '');       // e.g. controls-rearsets
  var TOP_SLUG = TOPCAT.replace(/^moto-/, '');      // e.g. controls

  // ── ONE subcategory vocabulary (S152 unification) ──────────────────────
  // The index asset ships GARA_SUBCATS {governed_tag: topslug} and
  // GARA_SUBCAT_ALIAS {legacy: governed} straight from taxonomy.py, so the
  // page side and the index side normalize with the SAME source. Before
  // this, legacy spellings ('fluids-oil-filters') vs governed ones
  // ('maintenance-oil-filters') made tier-1 dead on ~2.8k PDPs.
  function normSub(t) {
    var a = window.GARA_SUBCAT_ALIAS;
    return (a && a[t]) || t || '';
  }
  function topOf(t) {
    var s = window.GARA_SUBCATS;
    return (s && s[t]) || '';
  }
  function pageSubcat() {
    var t = normSub(SUB_TAG);
    if (t && topOf(t)) return t;
    // Products with no `top-` tag emit no data-subcat-handle (S152 QA #8):
    // derive from the raw tag list against the shipped vocabulary instead.
    var tags = (host.getAttribute('data-tags') || '').split(',');
    for (var i = 0; i < tags.length; i++) {
      var n = normSub(tags[i].trim());
      if (topOf(n)) return n;
    }
    return t;
  }

  function ymmReady() {
    var g = window.GaraYMM;
    if (!g || !g.get) return null;
    var v = g.get();
    return (v && v.year && v.make && v.model) ? v : null;
  }

  function bikeLabel(v) {
    return [v.year, v.make, v.model].filter(Boolean).join(' ');
  }

  function esc(s) { return String(s).replace(/</g, '&lt;'); }

  function cardsFrom(json, key) {
    var html = json && json[key];
    if (!html) return [];
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.prototype.slice.call(doc.querySelectorAll('.gara-recs-card'));
  }

  function fetchPool(handle) {
    if (!handle) return Promise.resolve([]);
    return fetch('/collections/' + handle + '?sections=gara-recs-pool',
                 { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return cardsFrom(j, 'gara-recs-pool'); })
      .catch(function () { return []; });
  }

  function fetchCard(handle) {
    return fetch('/products/' + handle + '?sections=gara-recs-card',
                 { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return cardsFrom(j, 'gara-recs-card')[0] || null; })
      .catch(function () { return null; });
  }

  function classifyProbe(sku, ymm) {
    var probe = { getAttribute: function (a) { return a === 'data-sku' ? sku : null; } };
    try { return window.GaraYMM.classifyViaIndex(probe, ymm); }
    catch (_) { return null; }
  }

  function fitsBike(card, ymm) {
    try { return window.GaraYMM.classify(card, ymm) === 'match'; }
    catch (_) { return false; }
  }

  function normSize(s) {
    return String(s || '').toUpperCase().replace(/\s+/g, '').replace(/ZR/g, 'R');
  }

  function fitsTire(card) {
    var sz = card.getAttribute('data-tire-size');
    if (!sz) return false;
    var g = window.GaraYMM;
    var sizes = g && g.getTireSizes ? g.getTireSizes() : null;
    if (!sizes) return false;
    var want = [sizes.front, sizes.rear, sizes.front_alt, sizes.rear_alt]
      .filter(Boolean).map(normSize);
    return want.indexOf(normSize(sz)) !== -1;
  }

  function loadHandleMap() {
    if (window.GARA_SKU_HANDLES) return Promise.resolve(window.GARA_SKU_HANDLES);
    var src = host.getAttribute('data-handles-src');
    if (!src) return Promise.resolve(null);
    return new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = function () { resolve(window.GARA_SKU_HANDLES || null); };
      s.onerror = function () { resolve(null); };
      document.head.appendChild(s);
    });
  }

  function loadIndex() {
    // PDPs do NOT ship gara-product-index.js (only the finder page does), so
    // without this lazy-load GARA_PRODUCT_INDEX is undefined here and EVERY
    // fit-aware lane (pool classify + index-resolve) silently skips — the
    // strip then falls through to the tire lane, which matches on spine tire
    // sizes instead of the index (S152: Continental tires recommended on a
    // sprocket PDP). Same lazy pattern as loadHandleMap; cached across pages.
    if (window.GARA_PRODUCT_INDEX) return Promise.resolve(window.GARA_PRODUCT_INDEX);
    var src = host.getAttribute('data-index-src');
    if (!src) return Promise.resolve(null);
    return new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = function () { resolve(window.GARA_PRODUCT_INDEX || null); };
      s.onerror = function () { resolve(null); };
      document.head.appendChild(s);
    });
  }

  function collect(ymm, done) {
    var picked = [];
    var seen = {};
    var fetched = [];                 // non-tire pool card arrays, in order
    seen[SELF_HANDLE] = true;
    // Normalized page categories — resolved here because the vocabulary
    // ships with the index asset, which loadIndex() has loaded by now.
    var subNorm = pageSubcat();
    var topNorm = topOf(subNorm) || TOP_SLUG;

    function take(cards, matcher) {
      for (var i = 0; i < cards.length && picked.length < LIMIT; i++) {
        var c = cards[i];
        var h = c.getAttribute('data-handle');
        if (!h || seen[h]) continue;
        if (!matcher(c)) continue;
        seen[h] = true;
        picked.push(c);
      }
    }

    function poolStep(handles, idx, next) {
      if (idx >= handles.length || picked.length >= LIMIT) { next(); return; }
      fetchPool(handles[idx]).then(function (cards) {
        fetched.push(cards);
        take(cards, function (c) { return fitsBike(c, ymm); });
        poolStep(handles, idx + 1, next);
      });
    }

    // P4 — index-resolve: fitting SKUs the pools couldn't reach, closest
    // category first, rendered by their own product pages.
    function resolveStep(next) {
      if (picked.length >= LIMIT) { next(); return; }
      var IDX = window.GARA_PRODUCT_INDEX;
      if (!IDX) { next(); return; }
      loadHandleMap().then(function (H) {
        if (!H) { next(); return; }
        // Closest-category-first in three scans, classifying ONLY candidates
        // in the current tier and stopping at the cap — the old build classified
        // every rows-entry in the whole index (40k+ classify calls) and then
        // did an O(n²) indexOf dedupe, hundreds of ms of main-thread jank for
        // at most 8 kept handles (S152 QA review).
        var cap = Math.max(0, LIMIT - picked.length) + 2;
        var want = [];
        var wseen = {};
        function scan(match) {
          for (var sku in IDX) {
            if (want.length >= cap) return;
            var e = IDX[sku];
            if (!e || !e.f || e.f.k !== 'rows') continue;
            if (!match(e)) continue;
            var h = H[sku];
            if (!h || seen[h] || wseen[h]) continue;
            if (classifyProbe(sku, ymm) !== 'match') continue;
            wseen[h] = true;
            want.push(h);
          }
        }
        function inSub(e) { return subNorm && normSub(e.s) === subNorm; }
        function inTop(e) {
          if (!topNorm || !e.s) return false;
          // Vocabulary shipped with the index; a STALE cached asset without
          // it must fall back to the old prefix rule, or tier-2 dies
          // silently (S152 verify catch).
          if (!window.GARA_SUBCATS) return e.s.indexOf(topNorm + '-') === 0;
          return topOf(normSub(e.s)) === topNorm;
        }
        if (subNorm) scan(inSub);
        if (want.length < cap && topNorm) scan(function (e) { return inTop(e) && !inSub(e); });
        if (want.length < cap) scan(function (e) { return !inSub(e) && !inTop(e); });
        if (!want.length) { next(); return; }
        Promise.all(want.map(fetchCard)).then(function (cards) {
          for (var k = 0; k < cards.length && picked.length < LIMIT; k++) {
            var c = cards[k];
            if (!c) continue;
            var h2 = c.getAttribute('data-handle');
            if (!h2 || seen[h2]) continue;
            seen[h2] = true;
            c.removeAttribute('hidden');
            picked.push(c);
          }
          next();
        });
      });
    }

    function tireStep(next) {
      if (picked.length >= LIMIT) { next(); return; }
      fetchPool('moto-tires').then(function (cards) {
        take(cards, fitsTire);
        next();
      });
    }

    function universalTopUp() {
      // FULL-STRIP guarantee: any remaining slots fill with UNIVERSAL-fit
      // items from the pools already in hand (chain lube, bolt kits —
      // honestly "fits your bike").
      for (var i = 0; i < fetched.length && picked.length < LIMIT; i++) {
        take(fetched[i], function (c) {
          try { return window.GaraYMM.classify(c, ymm) === 'universal'; }
          catch (_) { return false; }
        });
      }
      done(picked);
    }

    poolStep([subNorm ? 'moto-' + subNorm : SUBCAT,
              topNorm ? 'moto-' + topNorm : TOPCAT,
              'recs-top-picks'].filter(Boolean), 0, function () {
      resolveStep(function () {
        poolStep(['moto-maintenance'], 0, function () {
          tireStep(universalTopUp);
        });
      });
    });
  }

  function tireLinkTile(ymm) {
    var d = document.createElement('div');
    d.className = 'grid__item small--one-half medium-up--one-third';
    d.innerHTML =
      '<a href="/collections/moto-tires" class="gara-recs-tirelink" ' +
      'style="display:flex;flex-direction:column;justify-content:center;align-items:center;' +
      'min-height:180px;border:1px solid #e5e5e5;border-radius:6px;text-align:center;' +
      'padding:20px;text-decoration:none;">' +
      '<span style="font-size:1.1em;font-weight:600;">Tires for your ' +
      esc(bikeLabel(ymm)) + '</span>' +
      '<span style="margin-top:6px;opacity:.7;">Shop stock sizes &rsaquo;</span></a>';
    return d;
  }

  var nativeRef = null;   // the original strip, kept so a bike change/clear can restore it
  var ourBlock = null;    // our injected strip (replaced in place on re-render)

  function restoreNative() {
    if (ourBlock && ourBlock.parentNode && nativeRef) {
      ourBlock.parentNode.replaceChild(nativeRef, ourBlock);
      ourBlock = null;
    }
  }

  function render(cards, ymm) {
    var native = document.querySelector('product-recommendations');
    if (native) nativeRef = native;
    if (!nativeRef) return;
    var block = document.createElement('div');
    block.className = 'index-section';
    block.setAttribute('data-section-type', 'gara-recs-fit');
    block.innerHTML =
      '<div class="page-width"><header class="section-header">' +
      '<h3 class="section-header__title">' + esc(HEADING) + '</h3>' +
      '<p class="section-header__subtitle gara-recs-sub" style="margin-top:4px;opacity:.75;">' +
      'Fits your ' + esc(bikeLabel(ymm)) + '</p>' +
      '</header></div>' +
      '<div class="page-width page-width--flush-small"><div class="grid-overflow-wrapper">' +
      '<div class="product-recommendations page-width">' +
      '<div class="grid grid--uniform" data-gara-recs-grid></div>' +
      '</div></div></div>';
    var grid = block.querySelector('[data-gara-recs-grid]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].classList && cards[i].classList.contains('gara-recs-card')) {
        while (cards[i].firstChild) grid.appendChild(cards[i].firstChild);
      } else {
        grid.appendChild(cards[i]);
      }
    }
    if (ourBlock && ourBlock.parentNode) {
      ourBlock.parentNode.replaceChild(block, ourBlock);   // re-render (bike changed)
    } else {
      nativeRef.parentNode.replaceChild(block, nativeRef);
    }
    ourBlock = block;
    // Injected cards carry the theme's AOS image gate (<image-element
    // data-aos="image-fade-in"> stays opacity:0 until observed). AOS scans
    // only at init, so re-scan after injection — the same idiom theme.js
    // uses at its own inject sites. Without this every injected card is a
    // sized blank box (badge/title/price visible, image invisible).
    if (window.AOS) { AOS.refreshHard(); }
  }

  var running = false;
  function run() {
    var ymm = ymmReady();
    if (!ymm) { restoreNative(); return; }   // bike cleared -> native back
    if (running) return;
    running = true;
    // Index FIRST: pool classify and index-resolve both read
    // GARA_PRODUCT_INDEX, which PDPs don't ship natively (see loadIndex).
    // If it fails to load, DO NOTHING — without it every fit lane is blind
    // and the strip would degrade to tires-only over a destroyed native
    // strip (the S152 symptom). Native standing is the safe contract.
    loadIndex().then(function (IDX) {
      if (!IDX) { running = false; return; }
      collect(ymm, function (picked) {
        running = false;
        var now = ymmReady();        // bike may have changed mid-collect
        if (!now) { restoreNative(); return; }
        if (bikeLabel(now) !== bikeLabel(ymm)) { run(); return; }
        if (!picked.length) {
          var g = window.GaraYMM;
          var sizes = g && g.getTireSizes ? g.getTireSizes() : null;
          if (sizes && (sizes.front || sizes.rear)) render([tireLinkTile(ymm)], ymm);
          else restoreNative();      // nothing to say -> native strip stands
          return;
        }
        render(picked, ymm);
      });
    });
  }

  if (document.readyState !== 'loading') run();
  else document.addEventListener('DOMContentLoaded', run);
  // Re-render when the rider changes/clears the bike in the site-wide picker
  // (S152 QA: the strip used to keep claiming "Fits your <old bike>" forever).
  if (window.GaraYMM && window.GaraYMM.subscribe) {
    window.GaraYMM.subscribe(function () { run(); });
  }
})();
